import { City } from './profile';
import { ServiceCategory } from './service';
import {
  BudgetRange,
  QuoteRequest,
  RawQuoteTask,
  RawQuoteAlliance,
  QuoteAlliance,
  QuoteTask,
  QuoteOwner,
  mapUserQuoteTaskToQuoteTask,
  mapRawQuoteAllianceToQuoteAlliance,
} from './quote';
import { JobClient, JobStatus } from './common';
import { CreditActionType } from './credits';

export interface ListingType {
  id: number;
  code: string;
  credit_action_type?: CreditActionType | null;
}

export interface ShadowRecruiter {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string;
  company_type: 'individual' | 'company' | 'organization';
  job_title: string | null;
  phone: string | null;
  city_id: number | null;
  claimed_profile_id: string | null;
  invite_token: string | null;
  invite_url: string | null;
  invite_sent_at: string | null;
  claimed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShadowRecruiterInviteContext {
  shadow_recruiter_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string;
  company_type: 'individual' | 'company' | 'organization';
  job_title: string | null;
  phone?: string | null;
  city_id: number | null;
  claimed_at: string | null;
}

export interface ShadowRecruiterClaimResult {
  shadow_recruiter_id: string;
  profile_id: string;
  claimed_job_count: number;
}

export interface JobQuoteRaw {
  id: string;
  status: string;
  message: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  tasks: RawQuoteTask[];
  alliances: RawQuoteAlliance[];
  quote_owner_id: string;
  quote_owner_first_name: string;
  quote_owner_last_name: string;
  quote_owner_avatar_url: string | null;
  quote_owner_bio: string | null;
  quote_owner_services: Array<{
    id: number;
    slug: string;
    label: string;
  }>;
}

export interface JobQuote {
  id: string;
  status: string;
  message: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  tasks: QuoteTask[];
  alliances: QuoteAlliance[];
  quote_owner: QuoteOwner;
  change_request_message?: string | null;
}

// Raw data structure returned by fetch_user_jobs
export interface RawJob {
  quote_owner_avatar_url: string;
  quote_owner_first_name: string;
  id: string;
  title: string;
  description: string;
  on_site: boolean;
  start_date: string | null;
  end_date: string | null;
  published_at: string | null;
  expires_at: string | null;
  selection_date?: string | null;
  completed_at: string | null;
  status: string;
  languages: string[];
  visibility: string;
  created_at: string;
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  client_company_name: string;
  client_avatar_url: string;
  city_id: number;
  city_name: string;
  budget_range_id: number;
  budget_range_code: string;
  budget_range_min_amount: number;
  budget_range_max_amount: number;
  listing_type_id: number;
  listing_type?: ListingType;
  listing_type_credit_action_type: CreditActionType;
  listing_type_code: string;
  required_services: ServiceCategory[];
  quotes: JobQuoteRaw[];
  quote_request_status?: 'pending' | 'accepted' | 'rejected';
  shadow_recruiter_id?: string | null;
  shadow_recruiter_company_name?: string | null;
  is_shadow_recruiter?: boolean;
  recruiter_display_name?: string | null;
  recruiter_display_context?: string | null;
}

export interface Job {
  id: string;
  title: string;
  description: string;
  on_site: boolean;
  start_date: string | null;
  end_date: string | null;
  published_at: string | null;
  expires_at: string | null;
  selection_date?: string | null;
  completed_at: string | null;
  status: JobStatus;
  languages: string[];
  visibility: 'public' | 'private';
  city: City;
  client: JobClient;
  budget_range: BudgetRange;
  required_services: ServiceCategory[];
  listing_type: ListingType;
  created_at: string;
  updated_at: string;
  quote_requests?: QuoteRequest[];
  has_user_quote?: boolean;
  quotes?: JobQuote[];
  quote_request_status?: 'pending' | 'accepted' | 'rejected' | null;
  shadow_recruiter_id?: string | null;
  shadow_recruiter_company_name?: string | null;
  is_shadow_recruiter?: boolean;
  recruiter_display_name?: string | null;
  recruiter_display_context?: string | null;
  quotes_count?: number;
}

export function transformJobQuoteRawToJobQuote(jobQuoteRaw: JobQuoteRaw): JobQuote {
  const quoteOwner: QuoteOwner = {
    id: jobQuoteRaw.quote_owner_id,
    first_name: jobQuoteRaw.quote_owner_first_name,
    last_name: jobQuoteRaw.quote_owner_last_name,
    avatar_url: jobQuoteRaw.quote_owner_avatar_url ?? undefined,
    bio: jobQuoteRaw.quote_owner_bio ?? undefined,
    services: jobQuoteRaw.quote_owner_services,
  };

  const tasks: QuoteTask[] = jobQuoteRaw.tasks.map(mapUserQuoteTaskToQuoteTask);

  const alliances: QuoteAlliance[] = jobQuoteRaw.alliances.map(mapRawQuoteAllianceToQuoteAlliance);

  return {
    id: jobQuoteRaw.id,
    status: jobQuoteRaw.status,
    message: jobQuoteRaw.message,
    created_at: jobQuoteRaw.created_at,
    updated_at: jobQuoteRaw.updated_at,
    submitted_at: jobQuoteRaw.submitted_at,
    tasks,
    alliances,
    quote_owner: quoteOwner,
  };
}

export interface JobSearchResult {
  id: string;
  title: string;
  description: string;
  published_at: string | null;
  created_at: string;
  visibility: 'public' | 'private';
  status: string;
  start_date: string | null;
  end_date: string | null;
  expires_at: string | null;
  selection_date?: string | null;
  listing_type_id: number | null;
  listing_type_code: string | null;
  service_category_slugs: string[];
  industry_slugs: string[];
  languages: string[];
  city_id: number;
  city_name: string;
  on_site: boolean;
  budget_range_id: number;
  budget_range_code: string;
  budget_range_min_amount: number | null;
  budget_range_max_amount: number | null;
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  client_company_name: string | null;
  client_avatar_url: string | null;
  updated_at: string;
  has_user_quote: boolean;
  shadow_recruiter_id?: string | null;
  shadow_recruiter_company_name?: string | null;
  is_shadow_recruiter?: boolean;
  recruiter_display_name?: string | null;
  recruiter_display_context?: string | null;
}

export interface JobUpsertParams {
  title: string;
  description: string;
  budgetRange: BudgetRange;
  city: City;
  requiredServices: ServiceCategory[];
  onSite: boolean;
  languages: string[];
  startDate: string;
  endDate: string | null;
  shadowRecruiterId?: string | null;
}

export interface JobPriority {
  id: number;
  key: 'standard' | 'featured' | 'urgent';
  cost: number;
  label: string;
  description: string;
  details: string;
}
